import React from 'react';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';

// Import Local Styles
import styles from './input.css';

@inject('formsStore')
@observer
export default class Input extends React.Component {
    escapeRegExp(text) { // escapes characters in a string, so regExp works
        return text ? text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&') : undefined;
    }

    setFormData = (ev) => {
        ev.preventDefault();
        const { onChange, name, formsStore } = this.props;
        // set form data, passing name of field and value
        formsStore.setFormData(name, ev.target.value);
        if (onChange) onChange(ev);
    };

    clearValidationError = (ev) => {
        const { onFocus, name, formsStore } = this.props;
        // find Input and clear any validation issues
        formsStore.clearFieldError(name);
        if (onFocus) onFocus(ev);
    };

    validateField = (ev) => {
        const { onBlur, name, formsStore } = this.props;
        // find Input and validate it
        if (formsStore[`${name}Validation`]) formsStore[`${name}Validation`]();
        if (onBlur) onBlur(ev);
    };
    // allow character set
    allowedCharacterSets = {
        alphaOnly: 'A-Za-z',
        alphaNumericOnly: 'A-Za-z0-9',
        numericOnly: '0-9',
        latinOnly: 'A-zÀ-ÖØ-öø-ÿ',
        latinNumericOnly: 'A-zÀ-ÖØ-öø-ÿ0-9'
    };

    verifyCharacter = (ev) => {
        const { restrictCharacters, allowedCharacters, disallowedCharacters, max } = this.props;
        const { key,
            target: { value }
        } = ev;
        const allowed = new RegExp(`[${this.allowedCharacterSets[restrictCharacters]}${this.escapeRegExp(allowedCharacters) || ''}]`, 'g');
        const disallowed = new RegExp(`[${this.escapeRegExp(disallowedCharacters)}]`, 'g');
        if (
            // make sure you can press enter to submit the form if needed
            (typeof restrictCharacters !== 'undefined' && !allowed.test(key) && key !== 'Enter') ||
            // if disallowed characters
            (typeof disallowedCharacters !== 'undefined' && disallowed.test(key)) ||
            // for input type number, make max work correctly so it limits characters
            (typeof max === 'number' && value.length >= max)
        ) {
            ev.preventDefault();
        }
    };

    render() {
        const { autoComplete, id, name, mask,
            maxLength, max, placeholder, warning, type,
            error, defaultValue, guide, pattern, onWheel,
            disabled, inputMode, dataTest, modifier, onPaste, onKeyDown, setRef } = this.props;

        const classList = `${styles.input} ${error ? styles.error : ''} ${warning ? styles.warning : ''} ${modifier || ''}`;

        if (mask) {
            return (
                <MaskedInput
                    {...{
                        autoComplete,
                        disabled,
                        guide,
                        id,
                        inputMode,
                        mask,
                        max,
                        maxLength,
                        name,
                        onWheel,
                        pattern,
                        placeholder,
                        onPaste,
                        onKeyDown
                    }}
                    className={classList}
                    data-test={dataTest}
                    onBlur={this.validateField}
                    onChange={this.setFormData}
                    onFocus={this.clearValidationError}
                    onKeyPress={this.verifyCharacter}
                    type={type || 'text'}
                    value={defaultValue}
                    {...setRef && { ref: setRef }}
                />
            );
        }
        return (
            <input
                {...{
                    autoComplete,
                    disabled,
                    id,
                    inputMode,
                    max,
                    maxLength,
                    name,
                    onWheel,
                    pattern,
                    placeholder,
                    onPaste,
                    onKeyDown
                }}
                className={classList}
                data-test={dataTest}
                onBlur={this.validateField}
                onChange={this.setFormData}
                onFocus={this.clearValidationError}
                onKeyPress={this.verifyCharacter}
                type={type || 'text'}
                value={defaultValue}
                {...setRef && { ref: setRef }}
            />
        );
    }
}

Input.defaultProps = {
    autoComplete: 'on',
    dataTest: ''
};

Input.propTypes = {
    allowedCharacters: PropTypes.string,
    autoComplete: PropTypes.string,
    defaultValue: PropTypes.string,
    disallowedCharacters: PropTypes.string,
    onChange: PropTypes.func,
    error: PropTypes.string,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    maxLength: PropTypes.number,
    max: PropTypes.number,
    minCharacters: PropTypes.number,
    placeholder: PropTypes.string,
    restrictCharacters: PropTypes.string,
    type: PropTypes.string,
    dataTest: PropTypes.string
};
